import { apiClient, ApiResponse } from "./client";

// ============================================================================
// Types
// ============================================================================

// Component Types
export type ComponentType = 'video' | 'quiz' | 'text' | 'document' | 'interactive' | 'feedback'

export interface VideoComponentDto {
    id: number;
    type: 'video';
    title: string;
    description: string | null;
    videoUrl: string;
    videoProvider: string | null;
    thumbnailUrl: string | null;
    durationSeconds: number | null;
    sequenceOrder: number;
}

export interface QuizOptionDto {
    id: number;
    text: string;
    isCorrect: boolean;
    explanation: string | null;
}

export interface QuizComponentDto {
    id: number;
    type: 'quiz';
    questionText: string;
    questionType: string;
    options: QuizOptionDto[];
    points: number;
    isRequired: boolean;
    explanation: string | null;
    sequenceOrder: number;
}

export interface TextComponentDto {
    id: number;
    type: 'text';
    title: string;
    content: string;
    sequenceOrder: number;
}

export type LessonComponent = VideoComponentDto | QuizComponentDto | TextComponentDto

export interface LessonSummary {
    id: number;
    title: string;
    bareTitle: string | null;
    description: string | null;
    shortDescription: string | null;
    thumbnailUrl: string | null;
    qid: string | null;
    sequenceOrder: number;
    overallSequenceOrder?: number;
    estimatedDurationMinutes: number | null;
    components?: LessonComponent[];
}

export interface ModuleInCourse {
    id: number;
    title: string;
    bareTitle: string | null;
    description: string | null;
    shortDescription: string | null;
    thumbnailUrl: string | null;
    sequenceOrder: number;
    overallSequenceOrder?: number;
    lessons: LessonSummary[];
    estimatedDurationMinutes: number | null;
}

export interface CourseDetailResponse {
    id: number;
    title: string;
    bareTitle: string | null;
    description: string | null;
    shortDescription: string | null;
    thumbnailUrl: string | null;
    learningObjectives: string | null;
    concepts: string | null;
    starRating: number | null;
    estimatedDurationMinutes: number | null;
    modules: ModuleInCourse[];
}

export interface ModuleDetailResponse {
    id: number;
    title: string;
    bareTitle: string | null;
    description: string | null;
    shortDescription: string | null;
    thumbnailUrl: string | null;
    learningObjectives: string | null;
    concepts: string | null;
    sequenceOrder: number;
    estimatedDurationMinutes: number | null;
    lessons: LessonSummary[];
}

export interface LessonDetailResponse {
    id: number;
    title: string;
    bareTitle: string | null;
    description: string | null;
    shortDescription: string | null;
    thumbnailUrl: string | null;
    learningObjectives: string | null;
    concepts: string | null;
    qid: string | null;
    estimatedDurationMinutes: number | null;
    nextLessonId: number | null;
    prevLessonId: number | null;
}

/**
 * 동영상 자료실 스트리밍 정보 응답
 */
export interface MediaStreamInfoResponse {
    id: number;
    title: string;
    streamUrl: string;
    contentType: string;
    durationSeconds: number | null;
}

/**
 * Queture 영상 정보 응답
 */
export interface QuetureResponse {
    quetureId: string;
    title: string | null;
    hlsUrl: string | null;
    summaryHlsUrl?: string | null;
    thumbnailUrl: string | null;
    duration: number | null;
    hasSummary?: boolean;
    pauseAndGos?: PauseAndGoView[];
}

export interface PauseAndGoView {
    id: number;
    time: number;
    whenToGo: number | null;
    whenToGoBack: number | null;
    type: string;
    bundleItem: string | null;
    okButtonMessage: string | null;
    notOkButtonMessage: string | null;
    msg: string | null;
    pauseDuration: number | null;
    quizId: string | null;
    videoKey: string | null;
    published: boolean;
}

// ============================================================================
// Content Hierarchy Types (Generic for all content types)
// ============================================================================

export interface ContentHierarchyNode {
    id: number;
    title: string;
    bareTitle: string | null;
    type: string; // curriculum, course, module, lesson
    description: string | null;
    thumbnailUrl: string | null;
    qid: string | null;
    sequenceOrder: number;
    overallSequenceOrder?: number;  // 전체 레슨 순서 (순서 강제에 사용)
    estimatedDurationMinutes: number | null;
    children: ContentHierarchyNode[];
}

export interface ContentHierarchyResponse {
    id: number;
    title: string;
    bareTitle: string | null;
    type: string; // curriculum, course, module, lesson
    description: string | null;
    thumbnailUrl: string | null;
    estimatedDurationMinutes: number | null;
    children: ContentHierarchyNode[];
}

// ============================================================================
// API Functions
// ============================================================================

export const contentApi = {
    /**
     * 콘텐츠 계층구조 조회 (모든 콘텐츠 타입에 대응)
     */
    getContentHierarchy: async (
        contentId: number,
        includeInactive: boolean = false
    ): Promise<ContentHierarchyResponse> => {
        const response = await apiClient.get<
            ApiResponse<ContentHierarchyResponse>
        >(`/content/${contentId}/hierarchy`, {
            params: includeInactive ? { includeInactive: true } : undefined,
        });
        if (!response.data.data) {
            throw new Error("Content not found");
        }
        return response.data.data;
    },

    /**
     * Course 상세 조회
     */
    getCourseDetail: async (
        courseId: number
    ): Promise<CourseDetailResponse> => {
        const response = await apiClient.get<ApiResponse<CourseDetailResponse>>(
            `/content/courses/${courseId}`
        );
        if (!response.data.data) {
            throw new Error("Course not found");
        }
        return response.data.data;
    },

    /**
     * Lesson 상세 조회
     */
    getLessonDetail: async (
        lessonId: number
    ): Promise<LessonDetailResponse> => {
        const response = await apiClient.get<ApiResponse<LessonDetailResponse>>(
            `/content/lessons/${lessonId}`
        );
        if (!response.data.data) {
            throw new Error("Lesson not found");
        }
        return response.data.data;
    },

    /**
     * Queture 영상 정보 조회
     * @param contentId educational_contents.content_id (lesson의 content_id)
     * @param quetureId queture_ext.queture_id (QID)
     */
    getQuetureInfo: async (contentId: number, quetureId: string, includeUnpublishedPauseAndGo = false): Promise<QuetureResponse> => {
        const response = await apiClient.get<ApiResponse<QuetureResponse>>(
            `/content/${contentId}/quetures/${quetureId}`,
            { params: includeUnpublishedPauseAndGo ? { includeUnpublishedPauseAndGo: true } : undefined }
        );
        if (!response.data.data) {
            throw new Error("Queture not found");
        }
        return response.data.data;
    },

    /**
     * Queture 부가 콘텐츠 조회 (깨봉툰 인트로/아웃트로)
     */
    getQuetureContent: async (quetureId: string) => {
        const response = await apiClient.get<ApiResponse<{
            quetureId: string
            qnIntro?: Array<{ text?: string; imageBlobId?: string; audioBlobId?: string; imageUrl?: string; audioUrl?: string }>
            qnOutro?: Array<{
                text?: string; imageBlobId?: string; audioBlobId?: string; imageUrl?: string; audioUrl?: string
                answer0?: string; answer1?: string; correctAnswer?: 'answer_0' | 'answer_1'
            }>
            queboardConfig?: Array<{
                problem: { left: number; right: number }
                features: { tie: boolean; teleport5: boolean; teleport10: boolean; bank: boolean; indicator: boolean; customInput: boolean }
                tutorial: { enabled: boolean; modules: string[]; autoStart: boolean }
                character?: string
            }>
        }>>(`/content/queture-contents/${quetureId}`);
        return response.data.data ?? { quetureId }
    },

    /**
     * 동영상 자료실 스트리밍 정보 조회
     * @param mediaId media_library 테이블의 ID (configuration의 videoContentId)
     */
    getMediaStreamInfo: async (mediaId: number): Promise<MediaStreamInfoResponse> => {
        const response = await apiClient.get<ApiResponse<MediaStreamInfoResponse>>(
            `/content/media/${mediaId}/stream-info`
        );
        if (!response.data.data) {
            throw new Error("Media not found");
        }
        return response.data.data;
    },

    /**
     * Curriculum 상세 조회
     */
    getCurriculumDetail: async (
        curriculumId: number
    ): Promise<CurriculumDetailResponse> => {
        const response = await apiClient.get<
            ApiResponse<CurriculumDetailResponse>
        >(`/content/curricula/${curriculumId}`);
        if (!response.data.data) {
            throw new Error("Curriculum not found");
        }
        return response.data.data;
    },

    /**
     * Module 상세 조회
     */
    getModuleDetail: async (
        moduleId: number
    ): Promise<ModuleDetailResponse> => {
        const response = await apiClient.get<ApiResponse<ModuleDetailResponse>>(
            `/content/modules/${moduleId}`
        );
        if (!response.data.data) {
            throw new Error("Module not found");
        }
        return response.data.data;
    },
};

// ============================================================================
// Types (Supplemental)
// ============================================================================

export interface CourseInCurriculum {
    id: number;
    title: string;
    sequenceOrder: number;
    moduleCount: number;
    lessonCount: number;
    thumbnailUrl: string | null;
}

export interface CurriculumDetailResponse {
    id: number;
    title: string;
    description: string | null;
    shortDescription: string | null;
    thumbnailUrl: string | null;
    courses: CourseInCurriculum[];
    createdAt: string;
    updatedAt: string;
}
